import { useState } from 'react';
import { api } from '../api';
import type { MonthTotal } from '../api';
import { Link } from '../router';
import { useSession } from '../session';
import { useData } from '../useData';
import { Shell } from '../components/Shell';
import { Avatar, Empty, hours, Loading, useStatusName, when } from '../components/bits';

/** One client, at a glance: what is live, what was bought, what was spent, and
 *  when anybody last touched it.
 *
 *  A client may open their own. Every figure counts only what the reader can
 *  see, so a project the studio has taken out of their view is absent from
 *  their totals as well as from their list. */
export function Client({ id }: { id: number }) {
  const { t, locale, user } = useSession();
  const statusName = useStatusName();
  const [editing, setEditing] = useState(false);

  const summary = useData(() => api.workspaceSummary(id), [id]);
  const projects = useData(() => api.projects({ workspaceId: id }), [id]);

  if (summary.error) return <Shell><Empty>{t.notFound}</Empty></Shell>;
  if (!summary.data) return <Shell><Loading /></Shell>;

  const c = summary.data;
  const left = c.hoursBought - c.hoursUsed;

  return (
    <Shell>
      <div className="page-head">
        <div className="grow">
          <p className="kicker">{t.client}</p>
          <h1>{c.name}</h1>
        </div>
        {user?.isStaff && (
          <button onClick={() => setEditing((v) => !v)}>
            {editing ? t.cancel : t.editClient}
          </button>
        )}
      </div>

      {editing && (
        <EditClient
          id={id}
          name={c.name}
          isActive={c.isActive}
          onDone={() => { setEditing(false); summary.reload(); }}
          onCancel={() => setEditing(false)}
        />
      )}

      <div className="figures">
        <Figure label={t.projects} value={String(c.projectCount)} />
        <Figure label={t.tasks} value={String(c.openTasks)} hint={t.openTasks(c.openTasks)} />
        <Figure
          label={t.hours}
          value={`${hours(c.hoursUsed)}h`}
          // "-501h over" is negative twice. The sign is in the word.
          hint={c.hoursBought > 0
            ? `${c.hoursBought}h ${t.bought} · ${hours(Math.abs(left))}h ${left < 0 ? t.hoursOver : t.hoursLeft}`
            : undefined}
        />
        <Figure
          label={t.lastWorked}
          value={c.lastWorkedOn ? when(c.lastWorkedOn, locale) : '—'}
        />
      </div>

      <div className="split" style={{ marginTop: 'var(--sp-6)' }}>
        <section>
          <h2>{t.hoursByMonth}</h2>
          {c.byMonth.length === 0 ? <Empty>{t.noActivity}</Empty> : <Months months={c.byMonth} />}

          <h2 style={{ marginTop: 'var(--sp-6)' }}>{t.projects}</h2>
          {projects.data && projects.data.length > 0 ? (
            <table style={{ marginTop: 'var(--sp-3)' }}>
              <tbody>
                {projects.data.map((p) => (
                  <tr key={p.id}>
                    <td><Link to={`/projects/${p.id}`}>{p.title}</Link></td>
                    <td className="muted">{statusName(p.status)}</td>
                    <td className="num">{hours(p.usedHours)}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <Empty>{t.noProjects}</Empty>}
        </section>

        <aside className="side">
          <h2 style={{ fontSize: 'var(--fs-lead)' }}>{t.members}</h2>
          <ul className="files" style={{ marginTop: 'var(--sp-3)' }}>
            {c.members.map((m) => (
              <li key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                <Avatar name={m.name} />
                <span>{m.name}</span>
              </li>
            ))}
            {c.members.length === 0 && <li className="muted">—</li>}
          </ul>
        </aside>
      </div>
    </Shell>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="figure">
      <p className="kicker">{label}</p>
      <p className="value">{value}</p>
      {hint && <p className="muted" style={{ margin: 0, fontSize: 12 }}>{hint}</p>}
    </div>
  );
}

/** Twelve months as bars. No chart library: it is one number per month, and a
 *  row with a rule in it says the same thing. */
function Months({ months }: { months: MonthTotal[] }) {
  const peak = Math.max(...months.map((m) => m.hours), 1);
  return (
    <table style={{ marginTop: 'var(--sp-3)' }}>
      <tbody>
        {months.map((m) => (
          <tr key={m.month}>
            <td className="date muted" style={{ width: 80 }}>{m.month}</td>
            <td>
              <span className="bar" style={{ width: `${(m.hours / peak) * 100}%` }} />
            </td>
            <td className="num" style={{ width: 70 }}>{hours(m.hours)}h</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EditClient({
  id, name, isActive, onDone, onCancel,
}: {
  id: number;
  name: string;
  isActive: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useSession();
  const [form, setForm] = useState({ name, isActive });
  const [busy, setBusy] = useState(false);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.updateWorkspace(id, form);
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="card stack" style={{ marginBottom: 'var(--sp-5)' }}>
      <div style={{ maxWidth: 420 }}>
        <label htmlFor="ec-name">{t.clientName}</label>
        <input id="ec-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <label style={{ textTransform: 'none', letterSpacing: 0 }}>
        <input type="checkbox" checked={form.isActive}
          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          style={{ width: 'auto', marginRight: 8 }} />
        {t.active}
      </label>
      <div className="row">
        <button className="primary" type="submit" disabled={busy}>{t.save} &gt;</button>
        <button type="button" onClick={onCancel}>{t.cancel}</button>
      </div>
    </form>
  );
}
